import { useEffect, useMemo, useState } from 'react'
import * as UUID from 'uuid'

function load<EntityT, JsonT = EntityT>(props: LocalStorageState<EntityT, JsonT>, hostPrefix: string): EntityT {
    if (props.key) {
        console.debug('LocalStorage', `loading ${hostPrefix}-${props.key} from localstorage`)
        let jsonValues: JsonT = JSON.parse(localStorage.getItem(`${hostPrefix}-${props.key}`))
        if ([null, undefined].includes(jsonValues)) {
            console.debug('LocalStorage', `${hostPrefix}-${props.key} is empty in localstorage, falling back to defaultValues`)
            jsonValues = props.defaultValues as any
        }

        return jsonValues !== undefined ? props.fromJson(jsonValues) : ({} as any)
    }
    return props.defaultValues
}

export type LocalStorageState<EntityT, JsonT = EntityT> = {
    key?: string // When key is undefined, this hook acts as a useState hook.
    hostKey?: string
    defaultValues?: EntityT
    fromJson: (json: JsonT) => EntityT
    toJson: (values: EntityT) => JsonT
    load?: boolean // Set this to false if you want to block the initial load.
}

export type LocalStorageResult<EntityT> = {
    loaded: boolean
    values: EntityT
    setValues: (newValues: EntityT) => void
}

/**
 * This hook will load & persist the values to the LocalStorage using the given key. When a default value is provided it
 * will return this when LocalStorage is empty. When no key is provided, this hook acts as a useState hook.
 */
export default function useLocalStorage<EntityT, JsonT = EntityT>(props: LocalStorageState<EntityT, JsonT>): LocalStorageResult<EntityT> {
    const [hookId] = useState<string>(UUID.v4())
    const hostPrefix = useMemo<string>(() => (props.hostKey ? `${props.hostKey}` : 'global'), [props.hostKey])
    const [values, setValues] = useState<EntityT>()
    const [loaded, setLoaded] = useState<boolean>(false)

    useEffect(() => {
        if (props.load !== false) {
            const initValues = load(props, hostPrefix)
            setValues(initValues)
            setLoaded(true)
        } else {
            console.debug('LocalStorage', `'${hostPrefix}-${props.key}' is not yet ready to be loaded.`)
        }
    }, [props.hostKey, props.key, props.load])

    /**
     * Only load the values when the key matches, and we are not the hook that just set the values.
     */
    const handler = (evt: StorageEvent) => {
        if (evt.key === `${hostPrefix}-${props.key}` && evt.url !== hookId) {
            const newValues = load(props, hostPrefix)
            console.debug('LocalStorage', `'${hostPrefix}-${props.key}' values changed, updating...`)
            setValues(newValues)
        }
    }

    useEffect(() => {
        if (props.key) {
            window.addEventListener('storage', handler)
            return () => window.removeEventListener('storage', handler)
        }
        return () => {}
    }, [props.key, props.load])

    return {
        loaded,
        values,
        setValues: (newValues: EntityT) => {
            const oldValuesString = JSON.stringify(props.toJson(values))
            const newValuesString = JSON.stringify(props.toJson(newValues))
            if (loaded && oldValuesString !== newValuesString) {
                console.debug('LocalStorage', `values ${hostPrefix}-${props.key} changed, storing...`)
                setValues(newValues)
                if (props.key) {
                    localStorage.setItem(`${hostPrefix}-${props.key}`, newValuesString)
                    window.dispatchEvent(
                        new StorageEvent('storage', {
                            key: `${hostPrefix}-${props.key}`,
                            oldValue: oldValuesString,
                            newValue: newValuesString,
                            url: hookId,
                        }),
                    )
                }
            } else if (loaded) {
                console.debug('LocalStorage', `'${hostPrefix}-${props.key}' was updated, but values didn't change.`)
            } else {
                console.debug('LocalStorage', `'${hostPrefix}-${props.key}' is not yet loaded.`)
            }
        },
    }
}
